/**
 * AchievementManager — tracks and unlocks player achievements.
 *
 * Checks achievement conditions after each Dams to AGI stage ends.
 * Stores unlocks in localStorage + syncs to server.
 *
 * API:
 *   checkAfterStage(stats)    → newly unlocked achievements (array)
 *   getUnlocked()             → all unlocked achievement keys
 *   getAll()                  → all achievement definitions with unlock status
 *   getByCategory(category)   → definitions in one category
 *   getProgress()             → { unlocked, total }
 *   syncToServer()            → push local unlocks to D1
 */

import XAuth from "./XAuth";

const STORAGE_KEY = "woodchuck_achievements";

/** Build reserve left after a ~30 s climb (8,000 Wh draining 100 Wh/s). */
const FAST_CLEAR_RESERVE_WH = 5000;
/** Classic answer: a woodchuck would chuck about 700 lb of wood. */
const CLASSIC_ANSWER_LOGS = 700;

const cleared = (s) => s.status === "complete";
const clearedKind = (s, kind) => cleared(s) && s.kind === kind;

/**
 * Achievement definitions. Each has:
 *   key:         unique identifier (stored in D1 — never rename)
 *   name:        display name
 *   description: how to earn it
 *   category:    'skill' | 'progression' | 'economic'
 *   check:       (stats) => boolean — evaluated on StageSim `result()` stats
 *   icon:        emoji for display
 */
const ACHIEVEMENTS = [
  // ── Skill ──────────────────────────────────────────────────
  {
    key: "flawless_build",
    name: "FLAWLESS BUILD",
    description: "Clear a build stage without taking a hit",
    category: "skill",
    icon: "\uD83D\uDC77",
    check: (s) => clearedKind(s, "build") && s.hits === 0,
  },
  {
    key: "untouchable",
    name: "UNTOUCHABLE",
    description: "Clear a defend stage without taking a hit",
    category: "skill",
    icon: "\uD83D\uDEE1\uFE0F",
    check: (s) => clearedKind(s, "defend") && s.hits === 0,
  },
  {
    key: "high_water",
    name: "HIGH WATER",
    description: "Clear a build stage with 5,000+ Wh reserve left",
    category: "skill",
    icon: "\u23F1\uFE0F",
    check: (s) => clearedKind(s, "build") && s.reserveWh >= FAST_CLEAR_RESERVE_WH,
  },
  {
    key: "barrel_hopper",
    name: "BARREL HOPPER",
    description: "Jump 10 barrels in one stage",
    category: "skill",
    icon: "\uD83D\uDEE2\uFE0F",
    check: (s) => s.jumpsCleared >= 10,
  },
  {
    key: "beaver_bite",
    name: "BEAVER BITE",
    description: "Smash 3 barrels with Beaver Bite in one stage",
    category: "skill",
    icon: "\uD83E\uDDAB",
    check: (s) => s.smashes >= 3,
  },
  {
    key: "leech_buster",
    name: "LEECH BUSTER",
    description: "Pry 5 leeches off turbines in one stage",
    category: "skill",
    icon: "\uD83E\uDEB1",
    check: (s) => s.pried >= 5,
  },

  // ── Progression ────────────────────────────────────────────
  {
    key: "first_turbine",
    name: "FIRST TURBINE",
    description: "Build a broken ladder and spin up a turbine",
    category: "progression",
    icon: "\uD83C\uDF00",
    check: (s) => s.built >= 1,
  },
  {
    key: "stage_10",
    name: "POWER ON",
    description: "Reach stage 10",
    category: "progression",
    icon: "\uD83D\uDD0C",
    check: (s) => s.level >= 10,
  },
  {
    key: "stage_25",
    name: "GRID ONLINE",
    description: "Reach stage 25",
    category: "progression",
    icon: "\uD83D\uDCA1",
    check: (s) => s.level >= 25,
  },
  {
    key: "stage_50",
    name: "HALFWAY DAM",
    description: "Reach stage 50",
    category: "progression",
    icon: "\uD83C\uDF0A",
    check: (s) => s.level >= 50,
  },
  {
    key: "stage_80",
    name: "OPEN GATES",
    description: "Reach stage 80",
    category: "progression",
    icon: "\uD83E\uDD16",
    check: (s) => s.level >= 80,
  },
  {
    key: "stage_100",
    name: "FEED THE MACHINE",
    description: "Reach stage 100",
    category: "progression",
    icon: "\uD83E\uDDE0",
    check: (s) => s.level >= 100,
  },

  // ── Economic ───────────────────────────────────────────────
  {
    key: "first_log",
    name: "FIRST LOG",
    description: "Clear a stage and earn WOOD",
    category: "economic",
    icon: "\uD83E\uDEB5",
    check: (s) => cleared(s) && s.woodDelta > 0,
  },
  {
    key: "big_haul",
    name: "BIG HAUL",
    description: "Earn 500+ WOOD logs in one stage",
    category: "economic",
    icon: "\u26A1",
    check: (s) => s.woodDelta >= 500,
  },
  {
    key: "club_700_lb",
    name: "700 LB CLUB",
    description: "Hold 700+ WOOD logs — the classic answer",
    category: "economic",
    icon: "\uD83C\uDFC6",
    check: (s) => s.woodLogs >= CLASSIC_ANSWER_LOGS,
  },
  {
    key: "lumber_baron",
    name: "LUMBER BARON",
    description: "Hold 5,000+ WOOD logs",
    category: "economic",
    icon: "\uD83D\uDC51",
    check: (s) => s.woodLogs >= 5000,
  },
];

const DEFINED_KEYS = new Set(ACHIEVEMENTS.map((a) => a.key));

const AchievementManager = {
  /**
   * Check for newly unlocked achievements after a stage ends.
   *
   * @param {Object} stats - exactly what StageSim `result()` returns:
   *   status ('complete'|'failed'), level, kind ('build'|'defend'),
   *   bankWh, reserveWh, totalWh, woodLogs, woodDelta, timeMs, lives,
   *   maxCombo, jumpsCleared, smashes, built, pried, hits, taps
   * @returns {Array<Object>} newly unlocked achievements
   */
  checkAfterStage(stats) {
    const unlocked = AchievementManager.getUnlocked();
    const newlyUnlocked = [];

    for (const achievement of ACHIEVEMENTS) {
      if (unlocked.has(achievement.key)) continue;

      try {
        if (achievement.check(stats)) {
          newlyUnlocked.push(achievement);
          unlocked.add(achievement.key);
        }
      } catch {
        // Skip broken checks
      }
    }

    if (newlyUnlocked.length > 0) {
      AchievementManager._saveUnlocked(unlocked);
      AchievementManager.syncToServer().catch(() => {});
    }

    return newlyUnlocked;
  },

  /**
   * Get all unlocked achievement keys from localStorage.
   *
   * @returns {Set<string>}
   */
  getUnlocked() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) return new Set(JSON.parse(data));
    } catch {
      // Corrupted storage
    }
    return new Set();
  },

  /**
   * Get all achievement definitions with unlock status.
   *
   * @returns {Array<Object>} achievements with `unlocked: boolean`
   */
  getAll() {
    const unlocked = AchievementManager.getUnlocked();
    return ACHIEVEMENTS.map((a) => ({
      ...a,
      unlocked: unlocked.has(a.key),
    }));
  },

  /**
   * Get achievements by category.
   *
   * @param {string} category
   * @returns {Array<Object>}
   */
  getByCategory(category) {
    return AchievementManager.getAll().filter((a) => a.category === category);
  },

  /**
   * Get count of unlocked achievements.
   *
   * @returns {{ unlocked: number, total: number }}
   */
  getProgress() {
    // Ignore stale keys from retired achievements still in storage
    const unlocked = [...AchievementManager.getUnlocked()].filter((k) => DEFINED_KEYS.has(k));
    return { unlocked: unlocked.length, total: ACHIEVEMENTS.length };
  },

  /**
   * Save unlocked set to localStorage.
   * @param {Set<string>} unlocked
   */
  _saveUnlocked(unlocked) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...unlocked]));
    } catch {
      // Storage full
    }
  },

  /**
   * Sync local unlocks to server.
   */
  async syncToServer() {
    const token = XAuth.getSessionToken();
    if (!token) return;

    const unlocked = AchievementManager.getUnlocked();
    if (unlocked.size === 0) return;

    try {
      await fetch("/api/achievements", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ achievements: [...unlocked] }),
      });
    } catch {
      // Server sync is best-effort
    }
  },
};

export default AchievementManager;
